import { useEffect, useState } from "react";
import { Timer } from "@phosphor-icons/react";

/**
 * Compact SLA timer chip. Works in two modes:
 *  - "client" (default): computes status from lead fields purely client-side, fast for lists.
 *  - "fetch": loads /sla/leads/:id for the freshest server-computed value.
 * Tick rate: every 30s.
 */
export default function SlaTimer({ lead, size = "sm", mode = "client", stageOnly = null }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);

  if (!lead) return null;
  const paused = lead.status === "won" || lead.status === "lost";
  if (paused) {
    // Only render the paused chip when we're not filtering by a specific stage,
    // otherwise the cell stays empty (the table already shows the Won/Lost badge).
    if (stageOnly) return null;
    return (
      <span
        data-testid="sla-chip"
        title="SLA paused (closed lead)"
        className={`inline-flex items-center gap-1 rounded-sm bg-slate-100 text-slate-400 ${size === "lg" ? "px-2.5 py-1.5 text-xs" : "px-1.5 py-0.5 text-[0.65rem]"}`}
      >
        <Timer size={size === "lg" ? 12 : 10} />
        <span>—</span>
      </span>
    );
  }
  const stage = lead.first_response_at ? "close" : "first_response";
  if (stageOnly && stage !== stageOnly) return null;
  const dueIso = stage === "close" ? lead.sla_close_due_at : lead.sla_first_response_due_at;
  if (!dueIso) return null;
  const dueMs = new Date(dueIso).getTime();
  const remainingMs = dueMs - now;
  const receivedMs = new Date(lead.received_at || lead.created_at || dueIso).getTime();
  const totalMs = Math.max(1, dueMs - receivedMs);
  const elapsedMs = Math.max(0, now - receivedMs);
  const pct = Math.min(1, elapsedMs / totalMs);
  const breached = remainingMs <= 0;

  // Colour: green if pct < .75, amber if < 1, red if breached
  const tone = breached
    ? { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" }
    : pct >= 0.75
      ? { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" }
      : { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" };

  const label = breached
    ? `Overdue · ${formatDuration(-remainingMs)}`
    : `${formatDuration(remainingMs)} left`;

  const stageLabel = stage === "first_response" ? "First response" : "Time to close";

  return (
    <span
      data-testid="sla-chip"
      title={`${stageLabel}: ${label}`}
      className={`inline-flex items-center gap-1 rounded-sm border whitespace-nowrap ${tone.bg} ${tone.text} ${tone.border} ${size === "lg" ? "px-2.5 py-1 text-xs" : "px-1.5 py-0.5 text-[0.65rem]"}`}
    >
      <span className={`${tone.dot} h-1.5 w-1.5 rounded-full`} />
      <span className="font-medium">{label}</span>
    </span>
  );
}

function formatDuration(ms) {
  ms = Math.max(0, ms);
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const mins = totalMinutes % 60;
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
